import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { ApiError } from '../../api/client';
import { useDetailInView } from '../../components/ui/useDetailInView';
import { useAuth } from '../auth/AuthContext';
import { ARCHIVED_FILTERS, type ArchivedFilter } from '../components/api';
import type { Focus } from '../layout/focus';
import { useSelectedId } from '../layout/useSelectedId';
import { ProjectPanel } from './ProjectPanel';
import { createProject, listProjects, PROJECT_STATUSES, type ProjectStatus } from './api';
import { daysUntilDue, dueDateFromDays } from './due';

/** Projects on the left, the open one with its shortage report on the right. */
export function ProjectsPage({ focus }: { focus?: Focus | null }) {
  const { t } = useTranslation();
  const { can } = useAuth();
  const queryClient = useQueryClient();

  const [filter, setFilter] = useState<ProjectStatus | ''>('');
  const [selectedId, setSelectedId] = useSelectedId('/projects');
  const [creating, setCreating] = useState(false);

  const [archived, setArchived] = useState<ArchivedFilter>('hide');
  const projects = useQuery({
    queryKey: ['projects', 'list', filter, archived],
    queryFn: () => listProjects(filter || undefined, archived),
  });

  useEffect(() => {
    if (!focus) return;
    setCreating(false);
  }, [focus]);

  const detail = useDetailInView(`${selectedId}/${creating}`);

  const showingDetail = Boolean(selectedId || creating);

  return (
    <div className={`purchases${showingDetail ? ' is-detail' : ''}`}>
      <aside className="purchases__list">
        <div className="components__search">
          <select
            value={filter}
            onChange={(event) => setFilter(event.target.value as ProjectStatus | '')}
          >
            <option value="">{t('projects.anyStatus')}</option>
            {PROJECT_STATUSES.map((value) => (
              <option key={value} value={value}>
                {t(`projects.status.${value}`)}
              </option>
            ))}
          </select>
          {can('project.write') && (
            <button
              type="button"
              onClick={() => {
                setCreating(true);
                setSelectedId(null);
              }}
            >
              {t('projects.add')}
            </button>
          )}
        </div>

        {/* Hidden by default, mixed in on request, or on their own — the last
            is what makes the archive a place to look through. */}
        <select
          value={archived}
          onChange={(event) => setArchived(event.target.value as ArchivedFilter)}
        >
          {ARCHIVED_FILTERS.map((value) => (
            <option key={value} value={value}>
              {t(`common.archivedFilter.${value}`)}
            </option>
          ))}
        </select>

        {projects.isLoading && <p className="panel__hint">{t('common.loading')}</p>}
        {projects.data?.length === 0 && <p className="panel__hint">{t('projects.empty')}</p>}

        <ul className="components__results">
          {(projects.data ?? []).map((project) => (
            <li key={project.id}>
              <button
                type="button"
                className={[project.id === selectedId ? 'is-selected' : '', project.archived_at ? 'is-archived' : ''].filter(Boolean).join(' ') || undefined}
                onClick={() => {
                  setSelectedId(project.id);
                  setCreating(false);
                }}
              >
                <span className="components__name">
                  {project.name}
                  <span className={`purchase__status purchase__status--${project.status}`}>
                    {t(`projects.status.${project.status}`)}
                  </span>
                  {project.archived_at && (
                    <span className="badge badge--warn">{t('common.archived')}</span>
                  )}
                </span>
                <span className="components__meta">
                  <span className="components__code">{project.code}</span>
                  {project.items.length > 0 && (
                    <em>
                      {t('projects.unitsPlanned', {
                        n: project.items.reduce((sum, row) => sum + row.units, 0),
                      })}
                    </em>
                  )}
                </span>
              </button>
            </li>
          ))}
        </ul>
      </aside>

      <div className="purchases__detail" ref={detail}>
        <button
          type="button"
          className="pane-back"
          onClick={() => {
            setSelectedId(null);
            setCreating(false);
          }}
        >
          ← {t('common.backToList')}
        </button>

        {creating ? (
          <NewProjectForm
            onCreated={(id) => {
              setCreating(false);
              setSelectedId(id);
              void queryClient.invalidateQueries({ queryKey: ['projects'] });
            }}
            onCancel={() => setCreating(false)}
          />
        ) : selectedId ? (
          <ProjectPanel
            key={selectedId}
            projectId={selectedId}
            onArchived={() => setSelectedId(null)}
          />
        ) : (
          <p className="panel__hint">{t('projects.selectHint')}</p>
        )}
      </div>
    </div>
  );
}

function NewProjectForm({
  onCreated,
  onCancel,
}: {
  onCreated: (id: string) => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const [error, setError] = useState<string | null>(null);
  const [due, setDue] = useState('');
  const [dueDays, setDueDays] = useState('');

  const create = useMutation({
    mutationFn: (input: { name: string; due: string; note: string }) =>
      createProject({
        name: input.name,
        due_at: input.due || null,
        note: input.note || null,
      }),
    onSuccess: (project) => onCreated(project.id),
    onError: (exc) => setError(exc instanceof ApiError ? exc.code : 'unknown'),
  });
  const daysLeft = daysUntilDue(due);

  return (
    <form
      className="component-form"
      onSubmit={(event) => {
        event.preventDefault();
        const form = new FormData(event.currentTarget);
        create.mutate({
          name: String(form.get('name')),
          due,
          note: String(form.get('note') ?? ''),
        });
      }}
    >
      <h2>{t('projects.new')}</h2>

      <label>
        {t('projects.name')}
        <input name="name" required autoFocus />
      </label>

      <label>
        {t('projects.dueInDays')}
        <input
          type="number"
          min="0"
          step="1"
          value={dueDays}
          onChange={(event) => {
            const next = event.target.value;
            setDueDays(next);
            const calculated = dueDateFromDays(next);
            if (calculated) setDue(calculated);
            if (next === '') setDue('');
          }}
        />
      </label>

      <label>
        {t('projects.dueDate')}
        <input
          name="due_at"
          type="date"
          value={due}
          onChange={(event) => {
            const next = event.target.value;
            setDue(next);
            const days = daysUntilDue(next);
            setDueDays(days === null || days < 0 ? '' : String(days));
          }}
        />
        {due && daysLeft !== null && (
          <span className="component-form__parsed">
            {daysLeft >= 0
              ? t('projects.daysRemaining', { n: daysLeft })
              : t('projects.daysOverdue', { n: Math.abs(daysLeft) })}
          </span>
        )}
      </label>

      <label>
        {t('projects.note')}
        <textarea name="note" rows={4} />
        <span className="component-form__parsed">{t('common.markdownHint')}</span>
      </label>

      {error && (
        <p className="panel__error">
          {t(`projects.errors.${error}`, t('projects.errors.unknown'))}
        </p>
      )}

      <div className="component-form__actions">
        <button type="submit" disabled={create.isPending}>
          {t('common.save')}
        </button>
        <button type="button" onClick={onCancel}>
          {t('common.cancel')}
        </button>
      </div>
    </form>
  );
}
